import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { CaseFormRepository } from '../../../infrastructure/repositories/case-form.repository';
import { CaseFormState } from '../../states/case-form.state';
import { SensitivityCategoryEntity } from '../../../domain/entities/sensitivity-category.entity';

/**
 * HCMI-889: fetches the sensitivity categories available to classify form fields.
 * Categories come from the backend seed and drive the audit-log hard-hide filter,
 * so the values MUST match — never hardcode alternates in the frontend.
 *
 * Cached in state after the first call because the seed is global and effectively
 * immutable within a session.
 */
@Injectable({ providedIn: 'root' })
export class GetSensitivityCategoriesUseCase {
  private readonly _repository = inject(CaseFormRepository);
  private readonly _state = inject(CaseFormState);

  public execute(): Observable<SensitivityCategoryEntity[]> {
    const cached = this._state.sensitivityCategories();
    if (cached.length > 0) {
      return of(cached);
    }

    this._state.setLoadingSensitivityCategories(true);

    return this._repository.getSensitivityCategories().pipe(
      tap((categories) => this._state.setSensitivityCategories(categories)),
      finalize(() => this._state.setLoadingSensitivityCategories(false)),
    );
  }
}
